import React, { useEffect, useMemo, useRef, useState } from 'react';
import PermissionLink from '../../../components/PermissionLink';
import SearchSelect from '../../../components/SearchSelect';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import {
    coerceSchemaFieldValue,
    extValueToInput,
    SchemaFieldInput,
    useDictOptions,
} from '../deviceSchemaForm';
import {
    DeviceBundlePayload,
    DeviceBundlePreflight,
} from '../../../api/bundle';
import {
    archUsable,
    listProcessArchs,
    ProcessArch,
} from '../../../api/parameter';
import { listDict } from '../../../api/dict';
import { listTools, Tool } from '../../../api/toolbox';
import { SchemaGroupField } from '../../../api/schema';
import { IssueByPath } from './useDeviceBundleDraft';

// 设备向导第 ① 步: 设备主数据 + 能力字段.
//
// 能力字段 (capability 组) 放在第一步而不是第 ③ 步字段组里: 光源/波长/标准功率/光斑
// 这些是"这台机器是什么"的一部分, 建设备时就要填; 第 ③ 步留给其余可选字段组.
//
// 机型 (family) 2026-09-22 加回来并改回**必填** (撤销 09-20 的"留空以 deviceKey 作机型"):
// 参数模板按机型匹配, 一个机型一整套 (现网 16~20 张)。拿 deviceKey 兜底等于每台设备自成一个机型 ——
// 多头机 / 同系列不同瓦数拆出来的几行设备要各配一套模板, 下游按机型聚合也会碎成几条。
// 下拉列的是本框架已登记的机型码, 新机型走"+ 新建机型…"敲码, 落库时自动登记进 device_family 字典。
//
// 与原「新增设备」弹窗的关键差异: 那里是两段式写入 —— 先建设备再写 ext 值, 而新建走审批时
// 设备还没落库, ext 值无处可写只能丢弃, 审批通过后还得去工况页补录一遍。
// 套件是一个事务落全部内容, 这个洞在向导里不存在。

const CAPABILITY_GROUP = 'capability';

/** FAMILY_NEW 机型下拉里「新建」那一项的哨兵值 (与 DictSelect 的 __other__ 同套路). */
const FAMILY_NEW = '__new_family__';
const DEVICE_STATUS_OPTS = [
    { value: 'enabled', label: '启用' },
    { value: 'disabled', label: '禁用' },
];

interface Props {
    payload: DeviceBundlePayload;
    patch: (p: Partial<DeviceBundlePayload['device']>) => void;
    setPayload: (p: DeviceBundlePayload) => void;
    preflight: DeviceBundlePreflight | null;
    issueByPath: IssueByPath;
}

function StepDeviceBasic({
    payload,
    patch,
    setPayload,
    preflight,
    issueByPath,
}: Props) {
    const d = payload.device;
    // 拉字典的 effect 里要读当前机型, 但不该因为它变化而重拉字典 —— 用 ref 取值
    const familyRef = useRef(d.family || '');
    familyRef.current = d.family || '';
    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [tools, setTools] = useState<Tool[]>([]);
    // 本框架已登记的机型 (device_family 字典按框架分域, label = 设备名)
    // 只存机型码: 字典的 label 是当初建机型那台设备的名字 (如 ZH009 → M1 Ultra),
    // 与机型本身不是一回事, 跟在码后面只会让人以为自己在选设备.
    const [familyOpts, setFamilyOpts] = useState<string[]>([]);
    // 机型两态: 从已有里选 / 敲一个新的. 草稿里已有一个不在字典里的值时直接进"新建"态
    const [familyNew, setFamilyNew] = useState(false);
    const archOpts = useMemo(
        () =>
            archs.map((a) => ({
                value: a.code,
                label: `${a.icon} ${a.label}`,
            })),
        [archs],
    );
    const toolOpts = useMemo(
        () =>
            tools.map((t) => ({
                value: t.toolKey,
                label: t.name || t.toolKey,
            })),
        [tools],
    );
    const familySelectOpts = useMemo(
        () => [
            ...familyOpts.map((f) => ({ value: f, label: f })),
            { value: FAMILY_NEW, label: '+ 新建机型…' },
        ],
        [familyOpts],
    );

    useEffect(() => {
        listProcessArchs()
            .then((list) => setArchs(list.filter(archUsable)))
            .catch(() => setArchs([]));
        // 换算服务绑定是可选项, 拉不到 (无 tool 读权) 就不显示这个下拉
        listTools({ status: 'live', pageSize: 100 })
            .then((r) => setTools(r.list || []))
            .catch(() => setTools([]));
    }, []);

    useEffect(() => {
        if (!d.archCode) {
            setFamilyOpts([]);
            return;
        }
        let alive = true;
        listDict('device_family', { scope: d.archCode })
            .then((es) => {
                if (!alive) return;
                setFamilyOpts(es.map((e) => e.code));
                // 草稿恢复出来的机型如果不在本框架字典里, 直接落到"新建"态, 否则下拉选不中它
                setFamilyNew(
                    !!familyRef.current &&
                        !es.some((e) => e.code === familyRef.current),
                );
            })
            .catch(() => alive && setFamilyOpts([]));
        return () => {
            alive = false;
        };
    }, [d.archCode]);

    // 能力字段组: 从 preflight 取本框架的 capability 组定义
    const capGroup = useMemo(
        () =>
            (preflight?.deviceSchemas || []).find(
                (g) => g.groupKey === CAPABILITY_GROUP,
            ),
        [preflight],
    );
    const capFields = (capGroup?.fields || []) as unknown as SchemaGroupField[];
    const dictOpts = useDictOptions(capFields.map((f) => f.optionsSource));

    const capVals = (payload.extValues || {})[CAPABILITY_GROUP] || {};
    const setCapField = (key: string, raw: string) => {
        const cur = { ...capVals };
        const f = capFields.find((x) => x.key === key);
        if (raw === '') delete cur[key];
        else cur[key] = f ? coerceSchemaFieldValue(f, raw) : raw;
        const next = { ...(payload.extValues || {}) };
        if (Object.keys(cur).length === 0) delete next[CAPABILITY_GROUP];
        else next[CAPABILITY_GROUP] = cur;
        setPayload({ ...payload, extValues: next });
    };

    return (
        <>
            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">设备身份</div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>
                            device_key (业务主键)
                        </label>
                        <input
                            className="inp font-mono"
                            value={d.deviceKey}
                            onChange={(e) =>
                                patch({ deviceKey: e.target.value })
                            }
                            placeholder="如 S1-10W / DQ003-细切刀"
                        />
                        <FieldIssue issue={issueByPath['device.deviceKey']} />
                    </div>
                    <div>
                        <label className="field-label">
                            设备名称 (与源数据一致)
                        </label>
                        <input
                            className="inp"
                            value={d.name || ''}
                            onChange={(e) => patch({ name: e.target.value })}
                            placeholder="如 F1 Ultra / M2 / DT002"
                        />
                    </div>
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>工艺框架
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={d.archCode}
                            onChange={(v) =>
                                // 换框架 = 换整套字段组模板、模式清单与参数字段集, 已填的值不再适用.
                                // 参数行也必须清: 材料键粒度随框架变 (xcode / 膜材+基材 / 二级分类),
                                // 留着旧行提交时会报"材料键与框架粒度不符".
                                setPayload({
                                    ...payload,
                                    device: { ...d, archCode: v },
                                    extValues: {},
                                    modeRefs: [],
                                    modeSwitches: [],
                                    schemaBindings: undefined,
                                    params: [],
                                    thermalCal: [],
                                })
                            }
                            placeholder="请选择"
                            options={archOpts}
                        />
                        <FieldIssue issue={issueByPath['device.archCode']} />
                    </div>
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>机型
                            (family)
                        </label>
                        {familyNew ? (
                            <div className="flex items-center gap-2">
                                <input
                                    className="inp font-mono"
                                    autoFocus
                                    value={d.family || ''}
                                    onChange={(e) =>
                                        patch({ family: e.target.value })
                                    }
                                    placeholder="输入新机型码, 如 JS001"
                                />
                                <button
                                    type="button"
                                    className="text-xs text-brand-600 hover:underline whitespace-nowrap"
                                    onClick={() => {
                                        setFamilyNew(false);
                                        patch({ family: '' });
                                    }}
                                >
                                    选已有
                                </button>
                            </div>
                        ) : (
                            <SearchSelect
                                className="w-full"
                                value={d.family || ''}
                                disabled={!d.archCode}
                                onChange={(v) => {
                                    if (v === FAMILY_NEW) {
                                        setFamilyNew(true);
                                        patch({ family: '' });
                                        return;
                                    }
                                    patch({ family: v });
                                }}
                                placeholder={
                                    d.archCode ? '请选择机型' : '先选工艺框架'
                                }
                                options={familySelectOpts}
                            />
                        )}
                        <div className="text-[11px] text-slate-400 mt-1">
                            参数模板按机型匹配
                            (一个机型一整套)。多头机或同系列不同瓦数拆成的几行设备,
                            填同一个机型码即可共用一套模板; 新机型走「+
                            新建机型…」, 落库时自动登记进机型字典。
                        </div>
                        <FieldIssue issue={issueByPath['device.family']} />
                    </div>
                    <div>
                        <label className="field-label">
                            内部编号 (internalKey)
                        </label>
                        <input
                            className="inp font-mono"
                            value={d.internalKey || ''}
                            onChange={(e) =>
                                patch({ internalKey: e.target.value })
                            }
                            placeholder="可选"
                        />
                    </div>
                    <div>
                        <label className="field-label">状态</label>
                        <SearchSelect
                            className="w-full"
                            value={d.status || 'enabled'}
                            onChange={(v) => patch({ status: v })}
                            options={DEVICE_STATUS_OPTS}
                        />
                    </div>
                    {tools.length > 0 && (
                        <div>
                            <label className="field-label">
                                换算服务 (convertToolKey)
                            </label>
                            <SearchSelect
                                className="w-full"
                                value={d.convertToolKey || ''}
                                onChange={(v) => patch({ convertToolKey: v })}
                                placeholder="不绑定"
                                options={toolOpts}
                                clearable
                            />
                        </div>
                    )}
                    <div className={tools.length > 0 ? '' : 'col-span-2'}>
                        <label className="field-label">备注</label>
                        <input
                            className="inp"
                            value={d.remark || ''}
                            onChange={(e) => patch({ remark: e.target.value })}
                            placeholder="可选"
                        />
                    </div>
                </div>
            </div>

            {d.archCode && (
                <div className="card p-4 space-y-3">
                    <div className="text-sm font-semibold flex items-center gap-2">
                        能力字段
                        <span className="tag bg-brand-50 text-brand-700 font-normal">
                            Schema 驱动
                        </span>
                        <PermissionLink
                            resource="device_schema"
                            verb="update"
                            hide
                            to={`/devices/templates?focus=${encodeURIComponent(d.archCode)}`}
                            className="tag bg-slate-100 text-slate-500 font-normal hover:bg-brand-50 hover:text-brand-600"
                            title="表单按该框架模板渲染 · 点击查看/管理模板"
                        >
                            引用模板: {d.archCode} ↗
                        </PermissionLink>
                    </div>
                    {!capGroup || capFields.length === 0 ? (
                        <div className="text-xs text-amber-600">
                            框架 {d.archCode} 还没有 capability 字段组模板,
                            能力字段无法在这里填。 可先建设备,
                            之后在设备模板里补模板再到工况页录入。
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 gap-3">
                            {capFields.map((f) => (
                                <div key={f.key}>
                                    <label className="field-label">
                                        {f.label}
                                        {f.unit ? ` (${f.unit})` : ''}
                                    </label>
                                    <SchemaFieldInput
                                        field={f}
                                        value={extValueToInput(capVals[f.key])}
                                        onChange={(v) => setCapField(f.key, v)}
                                        dictOpts={dictOpts}
                                        selectClassName="w-full"
                                        inputClassName="inp w-full"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <FieldIssue
                        issue={issueByPath[`extValues.${CAPABILITY_GROUP}`]}
                    />
                </div>
            )}
        </>
    );
}

export default StepDeviceBasic;
